import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { create } from "zustand";
import { config } from "./config";

export type AuthStatus = "unavailable" | "loading" | "signed-out" | "signed-in";

export const useAuth = create<{ status: AuthStatus; email: string | null; error: string | null }>(
  () => ({ status: "unavailable", email: null, error: null }),
);

export const CALLBACK_PATH = "/auth/callback";

let manager: UserManager | undefined;

function userManager(): UserManager {
  if (manager) return manager;
  const { domain, issuer, clientId } = config.auth;
  const origin = window.location.origin;
  manager = new UserManager({
    authority: issuer,
    client_id: clientId,
    redirect_uri: `${origin}${CALLBACK_PATH}`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: "code",
    scope: "openid email profile",
    // Cognito's endpoints live on the custom domain; skip the discovery request.
    metadata: {
      issuer,
      authorization_endpoint: `${domain}/oauth2/authorize`,
      token_endpoint: `${domain}/oauth2/token`,
      revocation_endpoint: `${domain}/oauth2/revoke`,
    },
    loadUserInfo: false,
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });
  manager.events.addUserLoaded(setSignedIn);
  manager.events.addUserUnloaded(setSignedOut);
  manager.events.addSilentRenewError(() => {
    // Refresh token expired or revoked.
    void manager?.removeUser();
  });
  return manager;
}

function setSignedIn(user: User) {
  const email = typeof user.profile.email === "string" ? user.profile.email : null;
  useAuth.setState({ status: "signed-in", email, error: null });
}

function setSignedOut() {
  useAuth.setState({ status: "signed-out", email: null });
}

export function authConfigured(): boolean {
  return Boolean(config.auth.clientId && config.auth.issuer);
}

/** Restores the session, or completes sign-in when returning from the login page. */
export async function initAuth(): Promise<void> {
  if (!authConfigured()) return;
  useAuth.setState({ status: "loading" });
  const um = userManager();

  if (window.location.pathname === CALLBACK_PATH) {
    try {
      const user = await um.signinRedirectCallback();
      const returnTo = typeof user.url_state === "string" ? user.url_state : "/";
      window.history.replaceState(null, "", returnTo.startsWith("/") ? returnTo : "/");
      setSignedIn(user);
    } catch (error) {
      console.error("Sign-in failed", error);
      window.history.replaceState(null, "", "/");
      useAuth.setState({ status: "signed-out", email: null, error: "Sign-in failed. Try again." });
    }
    return;
  }

  let user = await um.getUser();
  if (user?.expired) {
    user = await um.signinSilent().catch(() => null);
    if (!user) await um.removeUser();
  }
  if (user) setSignedIn(user);
  else setSignedOut();
}

export function signIn(): Promise<void> {
  const { pathname, search } = window.location;
  return userManager().signinRedirect({ url_state: `${pathname}${search}` });
}

/** Ends the session here and on the login page. */
export async function signOut(): Promise<void> {
  const um = userManager();
  await um.revokeTokens(["refresh_token"]).catch(() => undefined);
  await um.removeUser();
  const params = new URLSearchParams({
    client_id: config.auth.clientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(`${config.auth.domain}/logout?${params}`);
}

/** A current access token, refreshed if it is about to expire; null when signed out. */
export async function getAccessToken(): Promise<string | null> {
  if (!authConfigured()) return null;
  const um = userManager();
  let user = await um.getUser();
  if (user && (user.expires_in ?? 0) < 60) {
    user = await um.signinSilent().catch(() => null);
  }
  return user?.access_token ?? null;
}
